import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { FormattedNumber } from 'react-intl';

import { Link } from '@sorare/routing';
import { css } from '@sorare/css';

import type { Rarity } from '#src/__generated__/globalTypes.ts';
import { Vertical } from '#src/atoms/layout/flex/index.tsx';
import { BrandS, LabelS } from '#src/atoms/typography/index.tsx';
import { PinButton } from '#src/components/navigation/AppNavigation/UserCurrencies/PinButton.tsx';
import { ANY_SPORT_ESSENCE_TYPE } from '#src/constants/__generated__/routes.ts';
import { useSportContext } from '#src/contexts/sport/index.ts';
import { useSeriesNav } from '#src/hooks/commonGame/useSeriesNav.ts';
import { usePinnedShardRarity } from '#src/hooks/config/usePinnedShardRarity.ts';
import { useIsLaptop } from '#src/hooks/device/useIsLaptop.ts';
import { formatScarcity } from '#src/lib/cards.ts';
import { getShardColor, useIsCurrentCardSet } from '#src/set/index.ts';
import { laptopAndAbove } from '#src/style/mediaQuery.ts';

import type { ShardRarityItem_cardShardsChest } from './__generated__/ShardRarityItem.graphql.ts';

const rarityWrapper = css`
  display: flex;
  flex-direction: column;
  gap: var(--unit);
  justify-content: space-between;
  align-items: center;
  flex: 1;
  text-align: center;
  border-radius: var(--unit);
  padding: var(--unit) 0;
  background-color: var(--c-surface-primary);

  @media ${laptopAndAbove} {
    flex-direction: row;
    width: 100%;
    padding: var(--half-unit) var(--unit);

    background-color: transparent;
    &:hover {
      background-color: var(--c-surface-primary);
    }
  }
`;

const rarityContainer = css`
  width: 100%;
  @media ${laptopAndAbove} {
    flex-direction: row;
  }
`;

type Props = {
  rarity: Rarity;
  cardShardsCount: number;
  cardSet: Nullable<{
    slug: string;
    name: string;
    branding?: Nullable<{ colors: { main?: Nullable<string> } }>;
  }>;
  onClick?: () => void;
};

export const ShardRarityItem = ({
  rarity,
  cardShardsCount,
  cardSet,
  onClick,
}: Props) => {
  const isCurrentCardSet = useIsCurrentCardSet();
  const { generateSportContextPath } = useSportContext();
  const isLaptop = useIsLaptop();
  const seriesNav = useSeriesNav();
  const { pinnedShardRarity, pinnedShardCardSet, updatePinnedShardRarity } =
    usePinnedShardRarity();

  const isPinned =
    pinnedShardRarity === rarity &&
    (!cardSet || pinnedShardCardSet === cardSet?.slug);

  const isCurrentSet = isCurrentCardSet(cardSet?.slug);

  return (
    <Vertical className={rarityContainer} justify="center" align="stretch">
      {!!isLaptop && !seriesNav && (
        <PinButton
          onClick={() => updatePinnedShardRarity(rarity, cardSet?.slug)}
          isPinned={isPinned}
        />
      )}
      <Link
        className={rarityWrapper}
        to={generateSportContextPath(ANY_SPORT_ESSENCE_TYPE, {
          params: { type: rarity },
        })}
        onClick={onClick}
      >
        <LabelS
          color={
            isCurrentSet
              ? getShardColor(rarity, cardSet?.branding)
              : 'var(--c-onBase-secondary)'
          }
        >
          {cardSet?.name ? cardSet?.name : formatScarcity(rarity)}
        </LabelS>
        <BrandS>
          <FormattedNumber value={cardShardsCount} />
        </BrandS>
      </Link>
      {!isLaptop && !seriesNav && (
        <PinButton
          onClick={() => updatePinnedShardRarity(rarity, cardSet?.slug)}
          isPinned={isPinned}
        />
      )}
    </Vertical>
  );
};

ShardRarityItem.fragments = {
  cardShardsChest: gql`
    fragment ShardRarityItem_cardShardsChest on CardShardsChest {
      id
      rarity
      cardShardsCount
      cardSet {
        id
        slug
        name
        branding {
          id
          colors {
            main
          }
        }
      }
    }
  ` as TypedDocumentNode<ShardRarityItem_cardShardsChest>,
};
